import React from "react";
import { FieldBadge } from "../field-badge";
import { Avatar } from "../../../profile_utils";
import { MultiSelectComponent } from "../../../../ui/input_multi_select/controller";
import { AddressAutocomplete } from "../address-autocomplete";

interface Profile {
  id: number;
  name: string;
  logo_url?: string;
  address?: string;
  address_id?: number;
  sectors?: string[];
  sector_ids?: number[];
  chalandise?: number;
}

interface VilleMainProfileProps {
  profile: Profile;
  secteurs?: any[];
}

export function VilleMainProfile({
  profile,
  secteurs = [],
}: VilleMainProfileProps) {
  const formatNumber = (value?: number) => {
    if (!value || value === 0) return "non renseignée";
    return new Intl.NumberFormat("fr-FR").format(value) + " hab.";
  };

  const formatSectors = (sectors?: string[]) => {
    if (!sectors || sectors.length === 0) return "non renseigné";
    if (sectors.length === 1) return sectors[0];
    return `${sectors[0]} (+${sectors.length - 1})`;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-2 border-b border-slate-400/60 h-[51px] items-center flex">
        <h3 className="font-semibold text-slate-800 text-lg">
          Votre collectivité
        </h3>
      </div>

      <div className="p-6 space-y-6">
        {/* Logo and Name */}
        <div className="flex items-center gap-3">
          <Avatar
            entityType="ville"
            imageUrl={profile.logo_url}
            name={profile.name}
            size="xl"
          />
          <div className="font-semibold text-slate-900">{profile.name}</div>
        </div>

        {/* Fields */}
        <div className="space-y-3 text-sm">
          {/* Localisation */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Localisation :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="ville"
                fieldName="address_id"
                label={profile.address || "non renseigné"}
                value={profile.address}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Localisation
                  </label>
                  <AddressAutocomplete
                    name="address_id"
                    value={profile.address_id}
                    displayValue={profile.address}
                    endpoint="/addresses"
                    placeholder="Rechercher une adresse..."
                  />
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Secteurs */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Secteurs :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="ville"
                fieldName="sector_ids"
                label={formatSectors(profile.sectors)}
                value={profile.sectors}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Secteurs d'activité
                  </label>
                  <MultiSelectComponent
                    name="sector_ids"
                    selectedIds={profile.sector_ids || []}
                    options={secteurs}
                    isMulti={true}
                    creatable={false}
                  />
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Chalandise */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Chalandise :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="ville"
                fieldName="chalandise"
                label={formatNumber(profile.chalandise)}
                value={profile.chalandise}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Zone de chalandise (habitants)
                  </label>
                  <input
                    type="number"
                    name="chalandise"
                    defaultValue={profile.chalandise}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
                  />
                </div>
              </FieldBadge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
